/**
 * Komponen bersama. Boleh diimpor domain mana pun; ia sendiri tidak boleh
 * mengimpor domain apa pun (ADR-043 — batas di dalam frontend).
 *
 * Semuanya kecil dan tanpa keadaan. Yang berkeadaan tinggal di domain atau shell.
 */
import {
  createContext, forwardRef, useContext, useEffect, useMemo, useRef, useState,
} from 'react'
import { useBahasa } from '../../app/i18n'
import { dariCron, jamDariFrekuensi, keCron } from '../lib/cron'

const cx = (...a) => a.filter(Boolean).join(' ')

/* ── Panel: kotak dasar setiap layar ─────────────────────────────────────── */
import { angka as angkaLokal } from '../lib/waktu'

export function Panel({ judul, catatan, aksi, children, className }) {
  return (
    // `overflow-hidden` menjaga anak-anaknya ikut lengkungan sudut.
    //
    // Tanpa ini, anak yang bersudut siku — pembungkus `overflow-x-auto` milik <Tabel>,
    // latar baris yang di-hover, garis `border-b` baris terakhir — digambar sampai tepi
    // persegi sementara garis panelnya melengkung. Yang terlihat: garis yang seolah
    // terpotong di pojok, atau warna latar yang menyembul keluar bingkai.
    // `min-w-0` WAJIB, dan ketiadaannya yang membuat kolom terakhir terpotong.
    //
    // Panel adalah anak dari `flex flex-col` di shell. Anak flex TIDAK menyusut di bawah
    // lebar min-content isinya kecuali `min-width` dipaksa nol. Jadi tabel yang lebar
    // MELEBARKAN PANELNYA alih-alih menggulir di dalamnya: `overflow-x-auto` milik
    // <Tabel> tidak pernah terpakai karena pembungkusnya ikut tumbuh, dan panelnya
    // menembus `max-w-[1240px]` sampai keluar layar.
    //
    // Yang hilang selalu kolom PALING KANAN — tombol aksi dan biaya. Operator
    // melaporkannya persis begitu: "semua yang ada tombol di akhir, biaya, itu terpotong".
    <section className={cx('bg-surface border border-line rounded-md overflow-hidden min-w-0', className)}>
      {(judul || aksi) && (
        <header className="flex items-center justify-between gap-3 px-3.5 py-2.5 border-b border-line">
          <div className="flex items-baseline gap-2 min-w-0">
            {judul && <h2 className="m-0 text-[13px] font-semibold truncate">{judul}</h2>}
            {catatan && <span className="text-[11px] text-ink3 truncate">{catatan}</span>}
          </div>
          {aksi}
        </header>
      )}
      {children}
    </section>
  )
}

export function Body({ children, className }) {
  return <div className={cx('p-3.5', className)}>{children}</div>
}

/* ── Tabel: selalu bisa digeser sendiri, badan halaman tidak pernah ikut ── */
export function Tabel({ kepala, children, kosong }) {
  return (
    // `w-max min-w-full` — dan urutan keduanya yang menyelesaikan kolom terpotong.
    //
    // `w-full` sendirian membuat tabel selebar pembungkusnya, lalu sel yang isinya tidak
    // muat MELUBER keluar kotaknya: garis `border-b` digambar di kotak selnya, isinya di
    // luar itu. Yang terlihat di layar: garis baris berhenti di tengah, sementara tombol
    // aksi dan angka biaya menggantung di luarnya tanpa garis.
    //
    // `w-max` membuat tabel selebar ISINYA — tidak pernah menyempit di bawah itu — dan
    // `min-w-full` menjaga ia tetap memenuhi pembungkus saat isinya justru sempit.
    // Pembungkus `overflow-x-auto` yang menggulir, dan itu baru mungkin sejak `Panel`
    // punya `min-w-0`.
    <div className="w-full overflow-x-auto">
      <table className="w-max min-w-full border-collapse text-[12.5px]">
        <thead>
          {/* Garis bawah kepala juga di <tr>, alasannya sama dengan baris isi. */}
          <tr className="border-b border-line">
            {kepala.map((h, i) => (
              <th
                key={i}
                className={cx(
                  'text-left text-[10px] font-semibold tracking-[.08em] uppercase text-ink3',
                  'px-3.5 py-2 whitespace-nowrap',
                  h?.num && 'text-right',
                )}
              >
                {h?.label ?? h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {children ?? (
            <tr>
              <td colSpan={kepala.length} className="px-3.5 py-8 text-center text-ink2">
                {kosong}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export function Baris({ children, onClick }) {
  return (
    <tr
      onClick={onClick}
      className={cx(
        // GARIS DI BARIS, BUKAN DI TIAP SEL.
        //
        // Bentuk lamanya menaruh `border-b` pada tiap `<td>`. Itu menggambar garis di
        // kotak SEL — jadi setiap sel yang kotaknya tidak selebar isinya (tombol, angka
        // rata-kanan) menampilkan garis yang berhenti sebelum isinya, atau tidak sama
        // sekali. Operator melaporkannya persis: "garis bawah menghilang di kolom yang
        // punya tombol atau angka".
        //
        // `border-collapse: collapse` membuat border pada `<tr>` digambar MENYAMBUNG
        // sepanjang baris. Satu garis, satu elemen — tidak ada lagi yang bisa putus per
        // kolom, apa pun isi selnya.
        //
        // `last:[&>td]:border-b-0` juga TIDAK PERNAH menghasilkan CSS sama sekali
        // (diperiksa di bundel terbangun); `last:border-b-0` di bawah menghasilkannya.
        'border-b border-line last:border-b-0',
        '[&>td]:px-3.5 [&>td]:py-2.5 [&>td]:align-middle',
        // `[&:hover>td]`, BUKAN `hover:[&>td]`.
        //
        // Yang kedua terlihat benar dan menghasilkan CSS yang salah: Tailwind
        // merangkainya jadi `.kelas > td:hover` — TD yang di-hover, bukan barisnya. Di
        // layar itu tampak sebagai satu sel menyala sementara sisanya gelap, dan operator
        // melaporkannya persis begitu 12 September. Diperiksa di CSS terbangun, bukan
        // ditebak dari kodenya.
        //
        // Varian arbitrer tunggal menulis seluruh selectornya sendiri: `&:hover > td`.
        onClick && 'cursor-pointer [&:hover>td]:bg-surface2',
      )}
    >
      {children}
    </tr>
  )
}

/* ── Chip: keadaan SELALU kata + warna, tidak pernah warna saja ──────────── */
const NADA = {
  ok: 'text-ok bg-okSoft',
  warn: 'text-warn bg-warnSoft',
  bad: 'text-bad bg-badSoft',
  idle: 'text-idle bg-idleSoft',
  accent: 'text-accent bg-accentSoft',
}

export function Chip({ nada = 'idle', titik = true, children }) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 whitespace-nowrap',
        'text-[11px] font-semibold px-1.5 py-0.5 rounded',
        NADA[nada] ?? NADA.idle,
      )}
    >
      {titik && <i className="w-[5px] h-[5px] rounded-full bg-current shrink-0" />}
      {children}
    </span>
  )
}

/* ── Tombol ──────────────────────────────────────────────────────────────── */
export function Tombol({ jenis = 'utama', kecil, className, ...sisa }) {
  const gaya =
    jenis === 'utama'
      ? 'bg-accent text-accentInk border-accent'
      : 'bg-surface text-ink2 border-lineStrong hover:text-ink hover:border-ink3'
  return (
    <button
      {...sisa}
      className={cx(
        'font-semibold rounded border cursor-pointer',
        kecil ? 'text-[11px] px-2 py-1' : 'text-[12px] px-2.5 py-1.5',
        'disabled:opacity-45 disabled:cursor-not-allowed',
        gaya,
        className,
      )}
    />
  )
}

/* ── Isian ───────────────────────────────────────────────────────────────── */
export function Isian({ className, ...sisa }) {
  return (
    <input
      {...sisa}
      className={cx(
        'w-full text-[12.5px] text-ink bg-surface border border-lineStrong rounded px-2.5 py-1.5',
        className,
      )}
    />
  )
}

export function Pilihan({ className, children, ...sisa }) {
  return (
    <select
      {...sisa}
      className={cx(
        'text-[12.5px] text-ink bg-surface border border-lineStrong rounded px-2 py-1.5 cursor-pointer',
        className,
      )}
    >
      {children}
    </select>
  )
}

/* ── Bilah tabel: cari + saring + jumlah, satu baris ─────────────────────── */
export function BilahTabel({ cari, saring, ringkas, children }) {
  const { t } = useBahasa()
  return (
    <div className="flex items-center gap-2 flex-wrap px-3.5 py-2.5 border-b border-line">
      {cari && (
        <Isian
          className="max-w-[250px]"
          placeholder={cari.placeholder ?? t('umum.cari')}
          value={cari.nilai}
          onChange={(e) => cari.set(e.target.value)}
        />
      )}
      {saring && (
        <Pilihan className="max-w-[190px]" value={saring.nilai} onChange={(e) => saring.set(e.target.value)}>
          {saring.opsi.map((o) => (
            <option key={o.nilai} value={o.nilai}>
              {o.label}
            </option>
          ))}
        </Pilihan>
      )}
      {/* SLOT BEBAS untuk penyaring yang tidak muat sebagai <Pilihan> — mis. <Seg>.
          Ditambahkan, bukan mengganti `saring`: pemanggil lama tidak berubah sama
          sekali, dan dropdown tetap bentuk yang benar untuk daftar panjang. */}
      {children}
      <span className="flex-1" />
      {ringkas && <span className="font-mono text-[11px] text-ink3 tabular">{ringkas}</span>}
    </div>
  )
}

/* ── Keadaan: memuat, gagal, kosong ──────────────────────────────────────── */
export function Memuat({ tinggi = 3 }) {
  return (
    <div className="p-3.5 flex flex-col gap-2" aria-busy="true">
      {Array.from({ length: tinggi }).map((_, i) => (
        <div key={i} className="h-6 rounded bg-surface2" />
      ))}
    </div>
  )
}

export function Gagal({ galat, ulangi }) {
  const { t } = useBahasa()
  // Jaringan mati dibedakan dari backend yang menolak. Yang pertama biasanya
  // salah pengguna atau dev mati; yang kedua pesannya datang dari server dan
  // ditampilkan apa adanya — lihat catatan di client.js soal bahasa.
  const pesan = galat?.kode === 'jaringan' ? t('umum.jaringanMati') : galat?.message
  return (
    <div className="p-5 text-center">
      <p className="m-0 text-[13px] font-semibold">{t('umum.gagal')}</p>
      {pesan && <p className="mt-1 mb-0 text-[12px] text-ink2">{pesan}</p>}
      {ulangi && (
        <Tombol jenis="hantu" className="mt-3" onClick={ulangi}>
          {t('umum.cobaLagi')}
        </Tombol>
      )}
    </div>
  )
}

/**
 * Kosong itu KEADAAN, bukan kesalahan — jadi ia menyebut apa yang bisa dilakukan,
 * bukan "No data". Aturan ini ditulis saat merancang lalu dilanggar di seluruh
 * mockup; di kode ia dipasang sebagai komponen supaya sulit dilewatkan.
 */
export function Kosong({ judul, keterangan, aksi }) {
  const { t } = useBahasa()
  return (
    <div className="px-3.5 py-8 text-center">
      <p className="m-0 text-[13px] font-semibold">{judul ?? t('umum.kosong')}</p>
      {keterangan && <p className="mt-1 mb-0 text-[12px] text-ink2 max-w-[52ch] mx-auto">{keterangan}</p>}
      {aksi && <div className="mt-3">{aksi}</div>}
    </div>
  )
}

/* ── Kepala halaman ──────────────────────────────────────────────────────── */
/**
 * Satu angka besar dengan labelnya.
 *
 * Dipindah dari `Pemakaian.jsx` 10 September saat tab Ikhtisar membutuhkannya juga.
 * Menyalinnya akan membuat dua kartu angka yang perlahan berbeda tinggi dan padding —
 * dan tidak ada yang menyadarinya sampai keduanya muncul di satu layar.
 *
 * `nilai` boleh string: kolom biaya mengirim "≥ $1.23" saat totalnya batas bawah
 * (ADR-058), dan memaksanya lewat pemformat angka akan membuang tandanya.
 */
export function Angka({ label, nilai, locale = 'id-ID' }) {
  const tampil = typeof nilai === 'number' ? angkaLokal(nilai, locale) : nilai
  return (
    <div className="bg-surface border border-line rounded-md px-3.5 py-2.5 min-w-[132px]">
      <b className="block text-[20px] tabular tracking-[-.01em]">{tampil}</b>
      <span className="text-[11.5px] text-ink2">{label}</span>
    </div>
  )
}

export function KepalaHalaman({ atas, judul, keterangan, aksi }) {
  return (
    <div className="flex items-end justify-between gap-6 flex-wrap">
      <div>
        {/* DI ATAS JUDUL, bukan di samping tombol aksi.
            Jalan keluar dari sebuah halaman detail dibaca sebelum judulnya, bukan
            sesudah — dan menaruhnya di deret aksi kanan membuatnya berbaris dengan
            tindakan yang mengubah data, tempat orang tidak mencarinya. */}
        {atas && <div className="mb-2.5">{atas}</div>}
        <h1 className="m-0 text-[21px] font-semibold tracking-[-.015em] text-balance">{judul}</h1>
        {keterangan && <p className="mt-1 mb-0 text-ink2 max-w-[64ch]">{keterangan}</p>}
      </div>
      {aksi}
    </div>
  )
}

/* ── Tab ─────────────────────────────────────────────────────────────────── */
export function Tab({ daftar, aktif, set }) {
  return (
    <div role="tablist" className="flex gap-0.5 border-b border-line overflow-x-auto">
      {daftar.map((d) => (
        <button
          key={d.id}
          role="tab"
          aria-selected={aktif === d.id}
          onClick={() => set(d.id)}
          className={cx(
            'text-[12.5px] px-3 py-2 whitespace-nowrap border-b-2 cursor-pointer bg-transparent',
            aktif === d.id
              ? 'text-ink font-semibold border-accent'
              : 'text-ink2 font-medium border-transparent hover:text-ink',
          )}
        >
          {d.label}
        </button>
      ))}
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * Primitif yang SUDAH dipakai berkas desain tapi belum pernah punya komponen.
 *
 * Sebelum ini tiap halaman menyalin kelas Tailwind-nya sendiri untuk bentuk yang
 * sama — bendera keterangan, kontrol bersegmen, baris label/nilai. Hasilnya lima
 * versi "kotak keterangan" dengan padding dan tebal garis yang beda-beda, dan
 * tidak ada satu tempat pun untuk memperbaikinya. Sekarang ada.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * BENDERA — kotak keterangan dengan garis tebal di kiri.
 *
 * Warnanya berarti: `info` menjelaskan yang BEKERJA, `warn` yang bekerja dengan
 * syarat, `bad` yang BELUM ADA. Membedakannya penting justru pada layar yang
 * sebagian fiturnya belum dibangun — pembaca harus bisa tahu mana catatan dan
 * mana batasan tanpa membaca seluruh paragrafnya.
 */
export function Bendera({ nada = 'warn', judul, children, className, ...sisa }) {
  const garis = {
    info: 'border-l-accent', warn: 'border-l-warn',
    bad: 'border-l-bad', ok: 'border-l-ok',
  }[nada] ?? 'border-l-warn'
  return (
    <div {...sisa} className={cx('bg-surface border border-line border-l-[3px] rounded-md px-3.5 py-2.5', garis, className)}>
      {judul && <p className="m-0 mb-0.5 text-[12.5px] font-semibold">{judul}</p>}
      {children && <div className="m-0 text-[12px] text-ink2 max-w-[80ch] [&_p]:m-0 [&_p+p]:mt-2">{children}</div>}
    </div>
  )
}

/**
 * SEG — pilihan bersegmen: satu baris tombol yang menyatu.
 *
 * Dipakai menggantikan <select> untuk pilihan yang JUMLAHNYA SEDIKIT DAN TETAP
 * (foto/video, satu model dari dua). Bedanya bukan selera: segmen menampilkan
 * seluruh pilihan sekaligus beserta harganya, sedangkan <select> menyembunyikan
 * semuanya kecuali yang terpilih — dan pilihan yang menagih biaya tidak boleh
 * disembunyikan di balik satu klik.
 *
 * Kalau daftarnya bisa panjang (katalog model yang tumbuh), pakai <Pilihan>.
 */
export function Seg({ nilai, set, daftar, className }) {
  return (
    <div className={cx('inline-flex border border-lineStrong rounded overflow-hidden', className)}>
      {daftar.map((d, i) => (
        <button
          key={d.id}
          type="button"
          aria-pressed={nilai === d.id}
          disabled={d.mati}
          title={d.judul}
          onClick={() => set(d.id)}
          className={cx(
            'text-[12px] px-2.5 py-1 cursor-pointer border-0',
            i > 0 && 'border-l border-lineStrong',
            'disabled:opacity-45 disabled:cursor-not-allowed',
            nilai === d.id
              ? 'bg-accent text-accentInk font-semibold'
              : 'bg-surface text-ink2 font-medium hover:text-ink',
          )}
        >
          {d.label}
        </button>
      ))}
    </div>
  )
}

/** Baris label/nilai. `KV` gridnya, `KVBaris` satu pasangnya. */
export function KV({ children, className }) {
  return (
    <dl className={cx('grid grid-cols-[150px_minmax(0,1fr)] gap-x-4 gap-y-2 m-0 text-[12.5px]', className)}>
      {children}
    </dl>
  )
}

export function KVBaris({ label, children }) {
  return (
    <>
      <dt className="pt-0.5 text-[11px] font-semibold uppercase tracking-[.06em] text-ink3">
        {label}
      </dt>
      <dd className="m-0 min-w-0">{children}</dd>
    </>
  )
}

/** Keterangan kecil di bawah sebuah isian. */
export function Sub({ children, className }) {
  return <span className={cx('block mt-1.5 text-[11px] text-ink3', className)}>{children}</span>
}

/** Baris tombol generate + harga. Harganya monospace supaya angkanya sejajar. */
export function BarisBuat({ children, harga }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
      {children}
      {harga && <span className="font-mono text-[10.5px] text-ink3">{harga}</span>}
    </div>
  )
}

/** Tombol generate — lebih kecil dan lebih lunak daripada <Tombol sekunder>. */
export function TombolBuat({ className, ...sisa }) {
  return (
    <button
      type="button"
      {...sisa}
      className={cx(
        'text-[11px] font-semibold px-2 py-[3px] rounded-[3px] whitespace-nowrap cursor-pointer',
        'bg-accentSoft text-accent border border-transparent hover:border-accent',
        'disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:border-transparent',
        className,
      )}
    />
  )
}

/**
 * ALUR — rantai keadaan: baru → scheduled → published.
 *
 * Ada di layar karena "Simpan" dan "Simpan sebagai draf" menghasilkan nasib yang
 * berbeda dan tidak terlihat: yang satu terbit sendiri pada jamnya, yang satu
 * tidak akan pernah terbit. Menuliskan rantainya lebih jujur daripada dua nama
 * tombol yang sama-sama berbunyi "simpan".
 */
export function Alur({ langkah }) {
  return (
    <div className="flex flex-wrap items-center gap-[7px] text-[11.5px]">
      {langkah.map((l, i) => (
        <span key={l.id ?? i} className="contents">
          {i > 0 && <span className="text-ink3">→</span>}
          <span
            className={cx(
              'font-mono text-[11px] px-2 py-[3px] rounded-[3px] border',
              l.akhir
                ? 'bg-okSoft text-ok border-transparent font-medium'
                : 'bg-surface2 text-ink2 border-line font-medium',
            )}
          >
            {l.label}
          </span>
        </span>
      ))}
    </div>
  )
}

/** Dua kolom yang menyatu jadi satu di layar sempit. */
export function Kolom2({ children, className }) {
  return (
    <div className={cx('grid gap-3.5 items-start [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))]', className)}>
      {children}
    </div>
  )
}

/** Textarea dengan gaya yang sama seperti <Isian>. */
// `forwardRef` supaya pemanggil bisa menyisipkan teks di posisi kursor (tombol tempat isian
// di layar Referensi). React 18 tidak meneruskan `ref` lewat props biasa.
export const AreaTeks = forwardRef(function AreaTeks({ className, baris = 3, ...sisa }, ref) {
  return (
    <textarea
      ref={ref}
      rows={baris}
      {...sisa}
      className={cx(
        'w-full text-[12.5px] text-ink bg-surface border border-lineStrong rounded px-2.5 py-1.5',
        'resize-y leading-[1.5]',
        className,
      )}
    />
  )
})

/**
 * KALENDER satu bulan — grid tujuh kolom, Senin di kiri.
 *
 * Tiap hari sebuah tombol, karena di layar "Ketersediaan" hari memang DITEKAN untuk
 * memblokirnya. Menampilkannya sebagai tabel lalu menaruh tombol terpisah di bawah
 * memaksa orang memilih tanggal dua kali: sekali dengan mata, sekali dengan isian.
 *
 * `tanda` memetakan 'YYYY-MM-DD' ke { nada, judul }. Yang TIDAK dilakukan di sini:
 * menebak hari kerja. Akhir pekan cuma diredupkan, tidak dimatikan — hari kerja tenant
 * ditentukan jadwal fitur `meeting` yang rutenya `tenant_admin`, dan menebaknya di klien
 * akan diam-diam salah untuk tenant yang bekerja Sabtu.
 */
export function Kalender({ bulan, tanda = {}, onPilih, sibuk }) {
  const [tahun, bln] = bulan.split('-').map(Number)
  const pertama = new Date(Date.UTC(tahun, bln - 1, 1))
  const jumlahHari = new Date(Date.UTC(tahun, bln, 0)).getUTCDate()
  // getUTCDay(): 0 Minggu … 6 Sabtu. Senin di kolom pertama, jadi Minggu jadi 6.
  const geser = (pertama.getUTCDay() + 6) % 7

  const NADA = {
    blokirSaya: 'bg-accent text-accentInk border-accent font-semibold',
    blokirTim: 'bg-warnSoft text-warn border-warn',
    meeting: 'bg-okSoft text-ok border-ok',
  }

  return (
    <div className="grid grid-cols-7 gap-1">
      {['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'].map((d) => (
        <span key={d} className="text-center text-[10.5px] font-semibold uppercase
                                 tracking-[.06em] text-ink3 pb-1">
          {d}
        </span>
      ))}

      {Array.from({ length: geser }, (_, i) => <span key={`k${i}`} />)}

      {Array.from({ length: jumlahHari }, (_, i) => {
        const hari = i + 1
        const iso = `${bulan}-${String(hari).padStart(2, '0')}`
        const t = tanda[iso]
        const akhirPekan = [5, 6].includes((geser + i) % 7)
        return (
          <button
            key={iso}
            type="button"
            title={t?.judul}
            disabled={sibuk}
            aria-pressed={t?.nada === 'blokirSaya'}
            onClick={() => onPilih?.(iso)}
            className={cx(
              'aspect-square rounded text-[12px] border cursor-pointer tabular',
              'disabled:opacity-45 disabled:cursor-not-allowed',
              t ? NADA[t.nada] : cx(
                'bg-surface border-line hover:border-ink3',
                akhirPekan ? 'text-ink3' : 'text-ink2',
              ),
            )}
          >
            {hari}
          </button>
        )
      })}
    </div>
  )
}

/**
 * JADWAL BERULANG — frekuensi + jam mulai + hari, bukan ekspresi cron.
 *
 * Desain `tenant-admin.html` menampilkan "4× sehari · tiap 6 jam · 00:00 06:00 12:00 18:00",
 * dan itu bukan sekadar selera: `0 9 * * *` menuntut orang yang mengaturnya tahu urutan
 * lima medan cron, dan salah satu digit yang meleset menghasilkan jadwal yang sah tapi
 * bukan yang dimaksud — tanpa satu pun galat.
 *
 * Cron TETAP bentuk simpanannya, karena tick scraping yang membacanya (`jalur_tren.jatuh_tempo`,
 * dulu n8n 01b). Yang berubah cuma siapa yang
 * merakitnya: kode, bukan orang.
 *
 * `dariCron` sengaja HANYA mengenali bentuk yang dirakitnya sendiri (`M H,H,H * * D`).
 * Jadwal yang ditulis tangan di luar layar ini tidak dipaksa masuk ke bentuk terdekat —
 * memaksanya berarti membuka layar lalu diam-diam mengubah jadwal yang tidak disentuh
 * siapa pun. Yang tidak dikenali dikembalikan apa adanya, dan layar mengatakannya.
 */
const HARI_ISO = [
  [1, 'Sen'], [2, 'Sel'], [3, 'Rab'], [4, 'Kam'], [5, 'Jum'], [6, 'Sab'], [7, 'Min'],
]

export { dariCron, jamDariFrekuensi, keCron }

export function Jadwal({ nilai, set, t }) {
  const terurai = dariCron(nilai)

  if (!terurai) {
    return (
      <div className="flex flex-col gap-2">
        <span className="font-mono text-[12px] text-ink2">{nilai || '—'}</span>
        <Bendera nada="warn">{t('tk.cronAsing')}</Bendera>
        <span>
          <Tombol
            kecil
            jenis="sekunder"
            onClick={() => set(keCron({ frekuensi: 1, mulai: 9, hari: [1, 2, 3, 4, 5] }))}
          >
            {t('tk.pakaiJadwalBiasa')}
          </Tombol>
        </span>
      </div>
    )
  }

  const { frekuensi, mulai, hari } = terurai
  const jam = jamDariFrekuensi(frekuensi, mulai)
  const ubah = (bagian) => set(keCron({ frekuensi, mulai, hari, ...bagian }))

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Seg
          nilai={String(frekuensi)}
          set={(v) => ubah({ frekuensi: Number(v) })}
          daftar={[1, 2, 3, 4, 6, 8].map((n) => ({ id: String(n), label: `${n}×` }))}
        />
        <span className="text-[11.5px] text-ink3">
          {t('tk.tiapJam', { n: Math.floor(24 / frekuensi) })}
        </span>
      </div>

      <label className="flex items-center gap-2">
        <span className="text-[11.5px] text-ink3">{t('tk.mulaiJam')}</span>
        <Pilihan value={String(mulai)} onChange={(e) => ubah({ mulai: Number(e.target.value) })}>
          {Array.from({ length: 24 }, (_, h) => (
            <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
          ))}
        </Pilihan>
      </label>

      <div className="flex flex-wrap items-center gap-1.5">
        {HARI_ISO.map(([d, label]) => {
          const aktif = hari.includes(d)
          return (
            <Tombol
              key={d}
              kecil
              aria-pressed={aktif}
              jenis={aktif ? 'utama' : 'sekunder'}
              // Hari terakhir tidak boleh dimatikan: jadwal tanpa hari sama sekali adalah
              // cron yang tidak pernah berjalan, dan layar akan menampilkannya seolah aktif.
              disabled={aktif && hari.length === 1}
              onClick={() => ubah({
                hari: aktif ? hari.filter((x) => x !== d) : [...hari, d].sort((a, b) => a - b),
              })}
            >
              {label}
            </Tombol>
          )
        })}
      </div>

      <span className="font-mono text-[11.5px] text-ink2">
        {jam.map((h) => `${String(h).padStart(2, '0')}:00`).join(' · ')}
      </span>
    </div>
  )
}

/* ── Simpan di kepala halaman ─────────────────────────────────────────────── */

/**
 * Satu tombol "Simpan perubahan" di kanan atas, untuk halaman bertab.
 *
 * MASALAHNYA BUKAN LETAK, MELAINKAN SIAPA YANG PUNYA FORMULIRNYA. Keadaan formulir hidup
 * di komponen TAB (Persona, SyaratMeeting, BagianScraping); tombolnya harus di kepala
 * HALAMAN. Mengangkat seluruh state ke induk akan membuat induk tahu bentuk keempat
 * formulirnya, dan tiap tab baru menyentuh berkas induk.
 *
 * Jadi tab MENDAFTARKAN fungsi simpannya, dan induk cuma memanggil yang terdaftar.
 *
 * BOLEH LEBIH DARI SATU PENDAFTAR, dan itu bukan keluwesan yang tidak dipakai: tab
 * "Jadwal balas" memuat DUA formulir — jadwalnya dan teks "di luar jam" — yang mutasinya
 * berbeda. Satu pendaftar berarti yang kedua diam-diam tidak tersimpan, dan tombol yang
 * menyimpan separuh formulir lebih buruk daripada dua tombol.
 *
 * Fungsinya di ref, bukan state: ia lahir baru tiap render (menutup state formulir), jadi
 * menyimpannya di state akan memicu render induk → render anak → fungsi baru → tanpa
 * henti. `tanda` hanya disetel saat benderanya benar-benar berubah.
 *
 * Tab yang TIDAK mendaftar — Model, Ketersediaan, Data prospek — menyimpan seketika tiap
 * tindakan, dan tombolnya TIDAK dirender sama sekali untuk mereka. Tombol yang muncul di
 * tab tanpa formulir adalah tombol yang ditekan lalu tidak melakukan apa-apa.
 */
/**
 * DUA KONTEKS, dan itu yang menutup loop tak berhingga.
 *
 * Versi pertama memakai satu konteks yang memuat `tanda`, dan itu berputar tanpa henti:
 *
 *   1. efek anak mendaftar    -> setTanda(objek BARU dari ringkasSimpan)
 *   2. tanda berubah identitas -> useMemo([tanda]) melahirkan ctx BARU
 *   3. deps efek anak memuat ctx -> efek jalan lagi -> kembali ke 1
 *
 * React melempar "Maximum update depth exceeded" dan HALAMANNYA MATI — bukan tombolnya
 * saja. Tidak satu pun pemeriksa melihatnya: `renderToString` tidak menjalankan efek.
 *
 * Jadi yang dipakai anak DIPISAH dari yang dipakai tombol. `KonteksDaftar` berisi ref dan
 * setter saja, di-memo dengan daftar kosong — identitasnya TIDAK PERNAH berubah, jadi efek
 * anak tidak pernah dijalankan ulang karenanya. `KonteksTanda` yang berubah, dan hanya
 * tombol yang membacanya.
 */
const KonteksDaftar = createContext(null)
const KonteksTanda = createContext(null)

export function PenyediaSimpan({ children }) {
  const daftar = useRef(new Map())
  const [tanda, setTanda] = useState(null)
  // Sengaja `[]`: nilainya cuma ref dan setter, keduanya stabil seumur komponen.
  const pendaftaran = useMemo(() => ({ daftar, setTanda }), [])
  return (
    <KonteksDaftar.Provider value={pendaftaran}>
      <KonteksTanda.Provider value={tanda}>{children}</KonteksTanda.Provider>
    </KonteksDaftar.Provider>
  )
}

let nomorSimpan = 0

/**
 * Dipanggil komponen TAB. `bisa` false mematikan tombolnya (mis. lokasi wajib kosong).
 *
 * Pendaftarannya dibatalkan saat tab berpindah — tanpa itu tombol di tab berikutnya masih
 * memegang fungsi tab sebelumnya, dan menekannya menyimpan formulir yang tidak sedang
 * dilihat siapa pun.
 */
export function useSimpanKepala(fn, { bisa = true, menyimpan = false } = {}) {
  const ctx = useContext(KonteksDaftar)
  const simpan = useRef(fn)
  simpan.current = fn
  const id = useRef(0)
  if (!id.current) id.current = ++nomorSimpan
  const bisaB = Boolean(bisa)
  const sedang = Boolean(menyimpan)

  useEffect(
    () => pasangSimpan(ctx, id.current, {
      jalan: () => simpan.current?.(), bisa: bisaB, menyimpan: sedang,
    }),
    [ctx, bisaB, sedang],
  )
}

/**
 * Mendaftar, lalu mengembalikan pembatalnya. DI LUAR `useEffect` supaya bisa diuji.
 *
 * Isi efek tidak bisa dijangkau harness ini sama sekali — `renderToString` tidak
 * menjalankan efek, dan proyek ini tidak punya jsdom. Jadi bagian yang punya keputusan
 * dikeluarkan jadi fungsi biasa, dan efeknya tinggal satu baris yang memanggilnya.
 *
 * PEMBATALANNYA YANG PALING PENTING. Tanpa ia, tombol di tab berikutnya masih memegang
 * fungsi tab sebelumnya: menekan "Simpan perubahan" di tab Model akan menyimpan formulir
 * Persona yang tidak sedang dilihat siapa pun, dengan nilai apa pun yang tertinggal di
 * sana. Itu penulisan diam-diam ke data klien, dan tidak ada satu pun galat yang muncul.
 */
export function pasangSimpan(ctx, kunci, entri) {
  if (!ctx) return undefined
  const segarkan = () => {
    const baru = ringkasSimpan(ctx.daftar.current)
    // Objek LAMA dikembalikan kalau isinya sama — React membatalkan render untuk state
    // yang identitasnya sama (`Object.is`). Tanpa ini tiap pendaftaran melahirkan objek
    // baru, dan objek baru adalah render baru walau isinya persis sama.
    ctx.setTanda((lama) => (samaTanda(lama, baru) ? lama : baru))
  }
  ctx.daftar.current.set(kunci, entri)
  segarkan()
  return () => {
    ctx.daftar.current.delete(kunci)
    segarkan()
  }
}

/**
 * Dua ringkasan yang ISINYA sama.
 *
 * Dipakai supaya `setTanda` mengembalikan objek LAMA saat tidak ada yang berubah — React
 * membatalkan render untuk state yang identitasnya sama (`Object.is`). Tanpa ini setiap
 * pendaftaran melahirkan objek baru, dan objek baru adalah render baru walau isinya
 * persis sama.
 */
export function samaTanda(a, b) {
  if (a === b) return true
  if (!a || !b) return false
  return a.bisa === b.bisa && a.menyimpan === b.menyimpan
}

/** SEMUA pendaftar harus siap; SATU yang sedang menyimpan sudah membuat tombolnya sibuk. */
export function ringkasSimpan(peta) {
  const isi = [...peta.values()]
  if (isi.length === 0) return null
  return {
    bisa: isi.every((x) => x.bisa),
    menyimpan: isi.some((x) => x.menyimpan),
  }
}

/**
 * Tombolnya. SELALU ADA, di tempat yang sama, di semua sub-menu.
 *
 * Versi pertama merendernya NIHIL untuk tab yang tidak punya formulir, dengan alasan yang
 * terdengar benar: tombol yang tidak menyimpan apa pun sebaiknya tidak ada. Yang terjadi
 * di layar justru lebih buruk — tombolnya muncul dan hilang tiap kali sub-menu berpindah,
 * dan orangnya mengira layarnya rusak atau perubahannya belum tersimpan.
 *
 * Sekarang ia selalu dirender. Tab yang menyimpan seketika mendapatkan tombol MATI yang
 * menyebut sebabnya lewat `title` — bukan tombol hidup yang tidak melakukan apa-apa, dan
 * bukan ruang kosong yang membuat kepala halaman melompat.
 */
export function TombolSimpanKepala({ t }) {
  const ctx = useContext(KonteksDaftar)
  const tanda = useContext(KonteksTanda)
  const otomatis = !tanda
  const bisa = tanda?.bisa ?? false
  const menyimpan = tanda?.menyimpan ?? false
  return (
    <Tombol
      disabled={otomatis || !bisa || menyimpan}
      title={otomatis ? t('umum.tersimpanOtomatis') : undefined}
      onClick={() => {
        if (!ctx) return
        for (const x of ctx.daftar.current.values()) x.jalan()
      }}
    >
      {menyimpan ? t('umum.menyimpan') : t('umum.simpanPerubahan')}
    </Tombol>
  )
}

/* ── Gambar yang berkasnya bisa hilang ────────────────────────────────────── */

/**
 * `<img>` yang MENGATAKAN kalau berkasnya tidak ada, bukan menampilkan ikon rusak.
 *
 * Berkas media hidup di disk, terpisah dari barisnya di database — dan keduanya bisa
 * berselisih. Itu bukan kemungkinan teoretis: 10 September sebuah referensi kehilangan
 * berkasnya sementara barisnya tetap ada, karena folder gambarnya belum tersimpan di
 * disk host (diperbaiki 12 September).
 *
 * Ikon rusak bawaan browser tidak menyebut apa pun. Orang yang melihatnya mengira
 * seluruh halaman bermasalah, memuat ulang, lalu melapor "layarnya error" — padahal yang
 * hilang satu berkas dan sisanya baik-baik saja.
 */
export function Gambar({ src, alt = '', className, teksHilang }) {
  const [hilang, setHilang] = useState(false)
  if (!src || hilang) {
    return (
      <span
        className={cx('grid place-items-center bg-surface2 border border-dashed',
                      'border-lineStrong text-center px-1', className)}
        title={teksHilang}
      >
        <span className="text-[9.5px] leading-tight text-ink3">{teksHilang}</span>
      </span>
    )
  }
  return (
    <img src={src} alt={alt} className={className} onError={() => setHilang(true)} />
  )
}
